if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        
        // Fullscreen toggle
        const fullscreenBtn = this.querySelector('.product-media-modal__toggle--fullscreen');
        if (fullscreenBtn) {
          fullscreenBtn.addEventListener('click', this.toggleFullscreen.bind(this));
        }

        // Close button logic overriding ModalDialog's aggressive closing
        const closeBtn = this.querySelector('[id^="ModalClose-"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this.closeRequested = true;
            this.hide();
          });
        }
        
        // Allow escape key to close
        this.addEventListener('keyup', (e) => {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.closeRequested = true;
            this.hide();
          }
        });

        // Share button
        const shareBtn = this.querySelector('.product-media-modal__toggle--share');
        if (shareBtn) {
          shareBtn.addEventListener('click', (e) => {
            const url = e.currentTarget.dataset.url;
            if (navigator.share) {
              navigator.share({ url: url });
            } else {
              navigator.clipboard.writeText(url);
            }
          });
        }

        // Thumbnails via event delegation (ensures it works even if rendered after connectedCallback)
        this.addEventListener('click', (e) => {
          const thumbnail = e.target.closest('.product-media-modal__thumbnail');
          if (thumbnail) {
            const mediaId = thumbnail.dataset.mediaId;
            this.scrollToMedia(mediaId);
          }
        });

        // Scroll listener and drag-to-scroll logic
        const content = this.querySelector('.product-media-modal__content');
        if (content) {
          // Initialize Mobile Finger Pinch-to-Zoom & Pan & Double-tap
          this.initTouchZoom(content);

          // Sync active media on native scroll
          content.addEventListener('scroll', () => {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => {
              this.syncActiveMediaOnScroll();
            }, 50);
          });

          // Prevent native image dragging
          content.addEventListener('dragstart', (e) => {
            e.preventDefault();
          });

          // Desktop Mouse Drag-to-scroll / Pan logic
          let isDown = false;
          let startX;
          let startY;
          let scrollLeft;
          let startPosX;
          let startPosY;

          content.addEventListener('mousedown', (e) => {
            if (this.currentScale && this.currentScale > 1.05) {
              // Mouse pan when zoomed in
              isDown = true;
              startX = e.clientX;
              startY = e.clientY;
              startPosX = this.zoomPosX || 0;
              startPosY = this.zoomPosY || 0;
              content.style.cursor = 'grab';
            } else {
              // Normal slide scroll when unzoomed
              isDown = true;
              startX = e.pageX - content.offsetLeft;
              scrollLeft = content.scrollLeft;
              content.style.cursor = 'grabbing';
              content.style.scrollSnapType = 'none';
            }
          });

          const stopDragging = () => {
            if (!isDown) return;
            isDown = false;
            content.style.cursor = '';
            if (this.currentScale && this.currentScale > 1.05) {
              // Clamp mouse pan
              if (this.clampAndApplyZoom) {
                this.clampAndApplyZoom();
              }
            } else {
              content.style.scrollSnapType = '';
              this.snapToClosest();
            }
          };

          content.addEventListener('mouseleave', stopDragging);
          content.addEventListener('mouseup', stopDragging);
          window.addEventListener('mouseup', stopDragging);

          content.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            e.preventDefault();
            if (this.currentScale && this.currentScale > 1.05) {
              const dx = e.clientX - startX;
              const dy = e.clientY - startY;
              this.zoomPosX = startPosX + dx;
              this.zoomPosY = startPosY + dy;
              if (this.activeMediaElement) {
                this.activeMediaElement.style.transition = 'none';
                this.activeMediaElement.style.transform = `translate3d(${this.zoomPosX}px, ${this.zoomPosY}px, 0px) scale(${this.currentScale})`;
              }
            } else {
              const x = e.pageX - content.offsetLeft;
              const walk = (startX - x);
              content.scrollLeft = scrollLeft + walk;
            }
          });
        }
      }

      initTouchZoom(content) {
        let scale = 1;
        let posX = 0;
        let posY = 0;
        let initialDist = 0;
        let initialScale = 1;
        let initialMid = { x: 0, y: 0 };
        let initialPos = { x: 0, y: 0 };
        let lastTouchPos = { x: 0, y: 0 };
        let isPinching = false;
        let isPanning = false;
        let lastTapTime = 0;
        let lastTapPos = { x: 0, y: 0 };
        let activeMedia = null;
        let activeSlide = null;

        const getActiveSlide = () => {
          const scrollLeft = content.scrollLeft;
          const width = content.clientWidth || window.innerWidth;
          const activeIndex = Math.max(0, Math.round(scrollLeft / width));
          const children = Array.from(content.children).filter(el => el.hasAttribute('data-media-id') || el.classList.contains('global-media-settings') || el.tagName === 'IMG');
          return children[activeIndex] || Array.from(content.children)[activeIndex] || Array.from(content.children)[0] || null;
        };

        const getMediaElement = (slide) => {
          if (!slide) return null;
          if (slide.tagName === 'IMG') return slide;
          const img = slide.querySelector('img');
          return img || slide;
        };

        const clampBounds = (targetScale, targetX, targetY) => {
          if (!activeMedia) return { x: 0, y: 0 };
          const rect = activeMedia.getBoundingClientRect();
          const unscaledW = rect.width / (scale || 1);
          const unscaledH = rect.height / (scale || 1);
          const scaledW = unscaledW * targetScale;
          const scaledH = unscaledH * targetScale;
          const maxPanX = Math.max(0, (scaledW - content.clientWidth) / 2);
          const maxPanY = Math.max(0, (scaledH - content.clientHeight) / 2);
          return {
            x: Math.max(-maxPanX, Math.min(maxPanX, targetX)),
            y: Math.max(-maxPanY, Math.min(maxPanY, targetY))
          };
        };

        const resetZoom = (animated = true) => {
          const target = activeMedia || (this.activeMediaElement || null);
          if (target) {
            if (animated) {
              target.style.transition = 'transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
              target.style.transform = 'translate3d(0px, 0px, 0px) scale(1)';
              setTimeout(() => {
                if (scale === 1 && target) {
                  target.style.transition = '';
                  target.style.transform = '';
                }
              }, 260);
            } else {
              target.style.transition = '';
              target.style.transform = '';
            }
          }
          // Reset any other media elements in modal content
          content.querySelectorAll('img, .global-media-settings, deferred-media, product-model').forEach(el => {
            if (el !== target) {
              el.style.transition = '';
              el.style.transform = '';
            }
          });

          scale = 1;
          posX = 0;
          posY = 0;
          this.currentScale = 1;
          this.zoomPosX = 0;
          this.zoomPosY = 0;
          this.activeMediaElement = null;
          isPinching = false;
          isPanning = false;
          content.style.overflowX = 'auto';
          content.style.scrollSnapType = 'x mandatory';
          content.style.touchAction = 'pan-x';
        };

        this.resetActiveZoom = resetZoom;

        this.clampAndApplyZoom = () => {
          if (!activeMedia) return;
          scale = Math.max(1, Math.min(4, scale));
          const clamped = clampBounds(scale, posX, posY);
          posX = clamped.x;
          posY = clamped.y;
          this.currentScale = scale;
          this.zoomPosX = posX;
          this.zoomPosY = posY;
          activeMedia.style.transition = 'transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
          activeMedia.style.transform = `translate3d(${posX}px, ${posY}px, 0px) scale(${scale})`;
        };

        const getDistance = (t1, t2) => {
          return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        };

        const getMidpoint = (t1, t2) => {
          return {
            x: (t1.clientX + t2.clientX) / 2,
            y: (t1.clientY + t2.clientY) / 2
          };
        };

        // Touch Start
        content.addEventListener('touchstart', (e) => {
          if (e.touches.length === 2) {
            // Pinch-to-zoom start
            isPinching = true;
            isPanning = false;
            activeSlide = getActiveSlide();
            activeMedia = getMediaElement(activeSlide);
            this.activeMediaElement = activeMedia;
            if (!activeMedia) return;

            activeMedia.style.transition = 'none';
            initialDist = getDistance(e.touches[0], e.touches[1]);
            initialScale = scale;
            initialMid = getMidpoint(e.touches[0], e.touches[1]);
            initialPos = { x: posX, y: posY };

            content.style.overflowX = 'hidden';
            content.style.scrollSnapType = 'none';
            content.style.touchAction = 'none';
          } else if (e.touches.length === 1) {
            if (scale > 1.05) {
              // Pan zoomed image
              isPanning = true;
              isPinching = false;
              if (activeMedia) activeMedia.style.transition = 'none';
              lastTouchPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
              content.style.touchAction = 'none';
            }
          }
        }, { passive: false });

        // Touch Move
        content.addEventListener('touchmove', (e) => {
          if (e.touches.length === 2 && isPinching) {
            e.preventDefault();
            if (!activeMedia) return;

            const currentDist = getDistance(e.touches[0], e.touches[1]);
            const currentMid = getMidpoint(e.touches[0], e.touches[1]);
            const pinchRatio = currentDist / (initialDist || 1);
            
            // Elastic zoom response during pinch
            scale = Math.max(0.75, Math.min(5.0, initialScale * pinchRatio));
            this.currentScale = scale;

            const deltaX = currentMid.x - initialMid.x;
            const deltaY = currentMid.y - initialMid.y;
            posX = initialPos.x + deltaX;
            posY = initialPos.y + deltaY;
            this.zoomPosX = posX;
            this.zoomPosY = posY;

            activeMedia.style.transform = `translate3d(${posX}px, ${posY}px, 0px) scale(${scale})`;
          } else if (e.touches.length === 1 && isPanning && scale > 1.05) {
            e.preventDefault();
            if (!activeMedia) return;

            const deltaX = e.touches[0].clientX - lastTouchPos.x;
            const deltaY = e.touches[0].clientY - lastTouchPos.y;
            lastTouchPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };

            posX += deltaX;
            posY += deltaY;
            this.zoomPosX = posX;
            this.zoomPosY = posY;

            activeMedia.style.transform = `translate3d(${posX}px, ${posY}px, 0px) scale(${scale})`;
          }
        }, { passive: false });

        // Touch End / Touch Cancel
        const handleTouchEnd = (e) => {
          if (e.touches.length === 1 && isPinching) {
            // Seamless transition from pinch to single-finger pan
            isPinching = false;
            isPanning = true;
            lastTouchPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          } else if (e.touches.length === 0) {
            if (isPinching || isPanning) {
              isPinching = false;
              isPanning = false;

              if (scale <= 1.05) {
                resetZoom(true);
              } else {
                scale = Math.max(1, Math.min(4, scale));
                const clamped = clampBounds(scale, posX, posY);
                posX = clamped.x;
                posY = clamped.y;
                this.currentScale = scale;
                this.zoomPosX = posX;
                this.zoomPosY = posY;
                if (activeMedia) {
                  activeMedia.style.transition = 'transform 0.25s cubic-bezier(0.25, 1, 0.5, 1)';
                  activeMedia.style.transform = `translate3d(${posX}px, ${posY}px, 0px) scale(${scale})`;
                }
              }
            }

            // Double Tap Detection
            if (e.changedTouches && e.changedTouches.length === 1) {
              const touch = e.changedTouches[0];
              const now = Date.now();
              const distFromLastTap = Math.hypot(touch.clientX - lastTapPos.x, touch.clientY - lastTapPos.y);

              if (now - lastTapTime < 300 && distFromLastTap < 30) {
                // Double tap detected!
                e.preventDefault();
                activeSlide = getActiveSlide();
                activeMedia = getMediaElement(activeSlide);
                this.activeMediaElement = activeMedia;

                if (scale > 1.1) {
                  // Zoom out
                  resetZoom(true);
                } else {
                  // Zoom in to 2.5x centered around tap point
                  if (activeMedia) {
                    scale = 2.5;
                    this.currentScale = scale;
                    const contentRect = content.getBoundingClientRect();
                    const centerX = contentRect.left + contentRect.width / 2;
                    const centerY = contentRect.top + contentRect.height / 2;
                    const tapOffsetX = touch.clientX - centerX;
                    const tapOffsetY = touch.clientY - centerY;
                    const targetX = -tapOffsetX * 1.5;
                    const targetY = -tapOffsetY * 1.5;
                    const clamped = clampBounds(scale, targetX, targetY);
                    posX = clamped.x;
                    posY = clamped.y;
                    this.zoomPosX = posX;
                    this.zoomPosY = posY;

                    content.style.overflowX = 'hidden';
                    content.style.scrollSnapType = 'none';
                    content.style.touchAction = 'none';
                    activeMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 1, 0.5, 1)';
                    activeMedia.style.transform = `translate3d(${posX}px, ${posY}px, 0px) scale(${scale})`;
                  }
                }
                lastTapTime = 0;
              } else {
                lastTapTime = now;
                lastTapPos = { x: touch.clientX, y: touch.clientY };
              }
            }
          }
        };

        content.addEventListener('touchend', handleTouchEnd);
        content.addEventListener('touchcancel', handleTouchEnd);

        // Double click on desktop for zoom
        content.addEventListener('dblclick', (e) => {
          activeSlide = getActiveSlide();
          activeMedia = getMediaElement(activeSlide);
          this.activeMediaElement = activeMedia;
          if (!activeMedia) return;
          if (scale > 1.1) {
            resetZoom(true);
          } else {
            scale = 2.5;
            this.currentScale = scale;
            const contentRect = content.getBoundingClientRect();
            const centerX = contentRect.left + contentRect.width / 2;
            const centerY = contentRect.top + contentRect.height / 2;
            const tapOffsetX = e.clientX - centerX;
            const tapOffsetY = e.clientY - centerY;
            const targetX = -tapOffsetX * 1.5;
            const targetY = -tapOffsetY * 1.5;
            const clamped = clampBounds(scale, targetX, targetY);
            posX = clamped.x;
            posY = clamped.y;
            this.zoomPosX = posX;
            this.zoomPosY = posY;

            content.style.overflowX = 'hidden';
            content.style.scrollSnapType = 'none';
            activeMedia.style.transition = 'transform 0.3s cubic-bezier(0.25, 1, 0.5, 1)';
            activeMedia.style.transform = `translate3d(${posX}px, ${posY}px, 0px) scale(${scale})`;
          }
        });
      }

      hide() {
        if (!this.closeRequested) return; // Prevent ModalDialog's pointerup from closing it
        if (this.resetActiveZoom) this.resetActiveZoom(false);
        super.hide();
        this.closeRequested = false;
        document.body.classList.remove('product-modal-open', 'overflow-hidden');
        if (typeof this.savedScrollY === 'number') {
          window.scrollTo({ top: this.savedScrollY, behavior: 'instant' });
        }
      }

      show(opener) {
        if (this.resetActiveZoom) this.resetActiveZoom(false);
        this.savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        this.closeRequested = false;
        super.show(opener);
        document.body.classList.add('product-modal-open', 'overflow-hidden');
        if (this.openedBy && this.openedBy.getAttribute('data-media-id')) {
          this.scrollToMedia(this.openedBy.getAttribute('data-media-id'));
        }
      }

      scrollToMedia(mediaId) {
        if (this.resetActiveZoom) this.resetActiveZoom(false);
        const targetElement = this.querySelector(`[data-media-id="${mediaId}"]:not(.product-media-modal__thumbnail)`);
        const container = this.querySelector('.product-media-modal__content');
        if (targetElement && container) {
          const index = Array.from(container.children).indexOf(targetElement);
          container.scrollTo({
            left: index * container.clientWidth,
            behavior: 'smooth'
          });
          this.updateUI(mediaId);
        }
      }

      snapToClosest() {
        const container = this.querySelector('.product-media-modal__content');
        if (!container) return;

        let closest = null;
        let minDiff = Infinity;

        Array.from(container.children).forEach((child) => {
          if (!child.hasAttribute('data-media-id') && !child.classList.contains('global-media-settings') && child.tagName !== 'IMG') return;
          const rect = child.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          const childCenter = rect.left + rect.width / 2;
          const containerCenter = containerRect.left + containerRect.width / 2;
          const diff = Math.abs(childCenter - containerCenter);

          if (diff < minDiff) {
            minDiff = diff;
            closest = child;
          }
        });

        if (closest) {
          const mediaId = closest.getAttribute('data-media-id');
          if (mediaId) this.scrollToMedia(mediaId);
        }
      }

      syncActiveMediaOnScroll() {
        const container = this.querySelector('.product-media-modal__content');
        if (!container) return;

        let closest = null;
        let minDiff = Infinity;

        Array.from(container.children).forEach((child) => {
          if (!child.hasAttribute('data-media-id') && !child.classList.contains('global-media-settings') && child.tagName !== 'IMG') return;
          const rect = child.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          const childCenter = rect.left + rect.width / 2;
          const containerCenter = containerRect.left + containerRect.width / 2;
          const diff = Math.abs(childCenter - containerCenter);

          if (diff < minDiff) {
            minDiff = diff;
            closest = child;
          }
        });

        if (closest) {
          const mediaId = closest.getAttribute('data-media-id');
          if (mediaId) {
            this.updateUI(mediaId);
          }
          
          const template = closest.querySelector('template');
          if (closest.nodeName == 'DEFERRED-MEDIA' && template && template.content.querySelector('.js-youtube')) {
            closest.loadContent();
          }
        }
      }

      updateUI(mediaId) {
        const thumbnails = Array.from(this.querySelectorAll('.product-media-modal__thumbnail'));
        let activeIndex = 0;
        
        thumbnails.forEach((thumbnail, index) => {
          if (thumbnail.dataset.mediaId === mediaId) {
            thumbnail.classList.add('is-active');
            activeIndex = index;
            thumbnail.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
          } else {
            thumbnail.classList.remove('is-active');
          }
        });

        const counter = this.querySelector('.product-media-modal__counter');
        if (counter && thumbnails.length > 0) {
          counter.textContent = `${activeIndex + 1} / ${thumbnails.length}`;
        }
      }
      
      toggleFullscreen() {
        // Request fullscreen on the document element so the fixed modal covers the whole screen correctly
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(err => {
            console.error(`Error attempting to enable fullscreen: ${err.message} (${err.name})`);
          });
        } else {
          document.exitFullscreen();
        }
      }
    }
  );
}
